import { useExternalOperatorBatchCheckpointsScanner } from '../../hooks/BatchCheckpoints/useExternalOperatorBatchCheckpointsScanner';
import { useExternalOperatorBulkCheckpointUpload } from '../../hooks/BatchCheckpoints/useExternalOperatorBulkCheckpointUpload';
import ConfirmationModal from '@/pages/Business/pages/BatchCheckpoints/components/ConfirmationModal';
import { childrenBatchCheckpointsExternalOperatorsPageBreadcrumbs } from '../../config/breadcrumbs';
import { useScannerFocus } from '@/pages/Business/pages/BatchCheckpoints/hooks';
import { LoaderBackdrop, NavigateLinks } from '@/components';
import { useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScannedItemsList,
  ScannerActions,
  ScannerPanel,
  BatchFailuresList,
  BulkUploadButton,
  CheckpointWarningDescription,
} from '@/pages/Business/pages/BatchCheckpoints/components';
import {
  WarningAmberOutlined as WarningIcon,
  ErrorOutlineOutlined as ErrorIcon,
} from '@mui/icons-material';

const BatchCheckpoints = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    transition,
    setTransition,
    transitionOptions,
    isTransitionsLoading,
    scannedItems,
    inputValue,
    setInputValue,
    isScannerActive,
    setIsScannerActive,
    isProcessing,
    isScanReady,
    handleScan,
    handleExecuteBatch,
    handleRemoveItem,
    handleClearItems,
    includedStatuses,
    isLoadingStatuses,
    isTransitionLocked,
    isCheckpointWarningModalOpen,
    dismissCheckpointWarning,
    batchFailures,
    dismissBatchFailures,
    scanError,
    dismissScanError,
  } = useExternalOperatorBatchCheckpointsScanner();

  const isAnyModalOpen = isCheckpointWarningModalOpen || batchFailures.length > 0 || !!scanError;

  useScannerFocus(inputRef, isScannerActive && isScanReady && !isAnyModalOpen);

  useEffect(() => {
    if (isScannerActive && isScanReady && !isCheckpointWarningModalOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isScanReady, isScannerActive, isCheckpointWarningModalOpen]);

  useEffect(() => {
    if (!isAnyModalOpen && isScanReady && isScannerActive) {
      const timer = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(timer);
    }
  }, [isAnyModalOpen, isScanReady, isScannerActive]);

  const breadcrumbs = useMemo(
    () => childrenBatchCheckpointsExternalOperatorsPageBreadcrumbs(navigate),
    [navigate]
  );

  const onScan = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const code = inputValue;
      setInputValue('');

      const input = inputRef.current;
      if (input) {
        input.value = '';
        input.blur();
      }

      await handleScan(code);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  return (
    <section className='flex flex-col w-full h-full gap-10'>
      <div className='w-full flex flex-col gap-3'>
        <NavigateLinks
          actualPage='Cambio de Estado Múltiple'
          breadcrumbs={breadcrumbs}
        />
        <div className='w-full flex justify-end'>
          <BulkUploadButton useUploadHook={useExternalOperatorBulkCheckpointUpload} />
        </div>
      </div>

      <section className='w-full h-full flex flex-col items-start'>
        <div className='w-full flex flex-col gap-8'>
          <ScannerPanel
            transition={transition}
            setTransition={setTransition}
            transitionOptions={transitionOptions}
            isTransitionsLoading={isTransitionsLoading}
            inputValue={inputValue}
            setInputValue={setInputValue}
            isScannerActive={isScannerActive}
            setIsScannerActive={setIsScannerActive}
            isSearchingShipping={isProcessing}
            isScanReady={isScanReady}
            isTransitionLocked={isTransitionLocked}
            includedStatuses={includedStatuses}
            isLoadingStatuses={isLoadingStatuses}
            inputRef={inputRef}
            onScan={onScan}
          />

          <div className='flex flex-col gap-8'>
            <ScannedItemsList
              items={scannedItems}
              onRemoveItem={handleRemoveItem}
              onClearAll={handleClearItems}
            />

            <ScannerActions
              transition={transition}
              count={scannedItems.length}
              onExecute={handleExecuteBatch}
              disabled={scannedItems.length === 0 || !transition || isProcessing}
            />
          </div>
        </div>
      </section>

      {isProcessing && <LoaderBackdrop />}

      <ConfirmationModal
        open={batchFailures.length > 0}
        onClose={dismissBatchFailures}
        onConfirm={dismissBatchFailures}
        title='Pedidos no procesados'
        icon={ErrorIcon}
        colorClass='bg-red-500'
        confirmLabel='Entendido'
        cancelLabel={null}
        description={<BatchFailuresList failures={batchFailures} />}
      />

      <ConfirmationModal
        open={!!scanError}
        onClose={dismissScanError}
        onConfirm={dismissScanError}
        title='Envío no procesado'
        icon={ErrorIcon}
        colorClass='bg-red-500'
        confirmLabel='Cerrar'
        cancelLabel={null}
        description={
          <div className='p-4 rounded-xl bg-red-50 border border-red-200'>
            <p className='text-red-900 text-[14px] leading-relaxed m-0'>
              <strong>Pedido {scanError?.shippingCode}</strong>: {scanError?.message}
            </p>
          </div>
        }
      />

      <ConfirmationModal
        open={isCheckpointWarningModalOpen}
        onClose={dismissCheckpointWarning}
        onConfirm={dismissCheckpointWarning}
        title='Antes de continuar'
        icon={WarningIcon}
        colorClass='bg-amber-500'
        confirmLabel='Entendido'
        cancelLabel={null}
        description={
          <CheckpointWarningDescription transitionLabel={transition?.label} />
        }
      />
    </section>
  );
};

export default BatchCheckpoints;
